import { memo, useMemo } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import type { FeatureData } from "@/types/spectrum";
import { groupFeaturesByObservation } from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";

interface MaterialDistributionProps {
  features: FeatureData[];
  /** MS 원본 결측(D19) — 물질 분포 산출 불가. 빈 파이 대신 안내 표시. */
  isMsMissing?: boolean;
}

const getSuspiciousColor = (suspicious: boolean) => {
  return suspicious ? "#ef4444" : "#10b981";
};

/** 파이 조각 상한 — 초과분은 "기타 관찰 N건" 한 조각으로 합산(값 보존). */
const MAX_SLICES = 11;
/** 기타 조각 색 — 위험/비위험 판정색을 쓰지 않는다(혼합 묶음이므로). */
const OTHER_COLOR = "#9ca3af";

export interface DistributionSlice {
  name: string;
  value: number;
  suspicious: boolean;
  isOther: boolean;
}

/**
 * 관찰 목록 → 파이 조각. 신호 상위 MAX_SLICES 개 + 나머지는 "기타" 한 조각
 * (값 보존 합산). 신호 미상(0)은 제외 — 지어내지 않는다.
 * (jsdom 은 recharts 를 그리지 않으므로 이 함수를 직접 테스트한다.)
 */
export function buildDistributionSlices(
  observations: ReturnType<typeof groupFeaturesByObservation>,
): DistributionSlice[] {
  const withSignal = observations
    .filter((o) => (o.features[0]?.area ?? 0) > 0)
    .map((o) => ({
      name:
        o.size > 1
          ? `${o.features[0].predicted_substance} 외 ${o.size - 1}`
          : o.features[0].predicted_substance,
      value: o.features[0].area,
      suspicious: o.features.some((f) => f.suspicious),
      isOther: false,
    }))
    .sort((a, b) => b.value - a.value);
  if (withSignal.length <= MAX_SLICES) return withSignal;
  const top = withSignal.slice(0, MAX_SLICES);
  const rest = withSignal.slice(MAX_SLICES);
  return [
    ...top,
    {
      name: `기타 관찰 ${rest.length}건`,
      value: rest.reduce((sum, item) => sum + item.value, 0),
      suspicious: false,
      isOther: true,
    },
  ];
}

const MaterialDistribution = ({
  features,
  isMsMissing = false,
}: MaterialDistributionProps) => {
  // 2026-08-31 (검출 표시 개선 1단계): 행 단위 → **관찰(스캔) 단위** 집계.
  //
  // 이전에는 후보 행마다 조각을 만들고 값은 area 가짜값(점수×10000)이었다 —
  // "물질 분포도"가 사실상 점수 파이였고, 같은 스캔의 대안 후보 5개가 같은
  // 신호를 5번 세는 구조였다(증거 중복). 이제 area 는 관찰의 실제 신호 합
  // (배경 제외)이고, 조각은 관찰당 1개(라벨 = 관찰 1순위 후보, 대안이 있으면
  // "외 N")다. 신호 미상(0)인 관찰은 파이에 넣지 않는다 — 지어내지 않는다.
  const observations = useMemo(
    () => groupFeaturesByObservation(features),
    [features],
  );
  const unknownSignalCount = useMemo(
    () => observations.filter((o) => (o.features[0]?.area ?? 0) <= 0).length,
    [observations],
  );
  // 관찰이 수십~백여 건이면 조각 전부가 0~4% 로 판독 불가다(교차 검토 실측:
  // 122조각). 신호 상위 관찰만 조각으로 그리고 나머지는 "기타 N건" 한 조각에
  // 합산한다 — 값을 버리지 않고 묶는다.
  const pieData = useMemo(
    () => buildDistributionSlices(observations),
    [observations],
  );

  // MS 원본 결측: 물질 분포는 동정 결과 기반이라 산출 불가 — 정직 안내.
  if (isMsMissing) {
    return (
      <div
        className={panelCls(
          "standard",
          "justify-center gap-4 flex flex-col m-auto h-full",
        )}
      >
        <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
          물질 분포도
        </h3>
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            MS 미수집 — 물질 분포 산출 불가
          </p>
          <p className="mt-2 text-xs text-gray-500">
            동정 결과가 없어 분포를 계산할 수 없음 (결측이며 비검출 아님)
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={panelCls(
        "standard",
        "justify-between gap-10 flex flex-col m-auto h-full",
      )}
    >
      <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
        <span className="w-2 h-2 bg-pink-500 rounded-full mr-2"></span>
        물질 분포도
        <span className="ml-2 text-xs font-normal text-gray-500">
          관찰(스캔) 신호 기준 · 라벨은 관찰 1순위 후보
        </span>
      </h3>
      {pieData.length === 0 ? (
        <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-8 text-center">
          <p className="text-sm font-medium text-gray-700">
            {observations.length === 0
              ? "AI 후보 없음 — 분포 없음"
              : "신호 정보 없음 — 분포를 그리지 않습니다"}
          </p>
          <p className="mt-2 text-xs text-gray-500">
            {observations.length === 0
              ? "동정된 후보가 없어 분포를 계산할 대상이 없음"
              : "관찰의 스캔 신호를 확인할 수 없어 비율을 계산하지 않음"}
          </p>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <PieChart>
            <Pie
              data={pieData}
              cx="50%"
              cy="50%"
              labelLine={true}
              label={({ name, percent }) =>
                `${name}\n${percent ? (percent * 100).toFixed(0) : 0}%`
              }
              outerRadius={100}
              fill="#8884d8"
              dataKey="value"
              style={{ fontSize: "10px" }}
              isAnimationActive={false}
            >
              {/* 셀은 pieData 와 1:1 — 이전에는 features 를 돌아 관찰 집계와
                어긋날 수 있었다 */}
              {pieData.map((entry, i) => (
                <Cell
                  key={`cell-${entry.name}-${i}`}
                  fill={
                    entry.isOther
                      ? OTHER_COLOR
                      : getSuspiciousColor(entry.suspicious)
                  }
                />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      )}
      {unknownSignalCount > 0 && pieData.length > 0 && (
        <p className="text-[11px] text-gray-500">
          신호 미상 관찰 {unknownSignalCount}건은 비율에 넣지 않았습니다.
        </p>
      )}
      <div className="mt-3 space-y-2">
        <div className="text-xs font-medium text-gray-700">위험도 분류</div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-red-500 rounded-full"></div>
          <span className="text-xs text-gray-600">위험 물질</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-green-500 rounded-full"></div>
          <span className="text-xs text-gray-600">안전 물질</span>
        </div>
        <div className="flex items-center space-x-2">
          <div className="w-3 h-3 bg-gray-400 rounded-full"></div>
          <span className="text-xs text-gray-600">
            기타 — 신호 하위 관찰 묶음 (위험/비위험 혼합)
          </span>
        </div>
      </div>
    </div>
  );
};

export default memo(MaterialDistribution);
